import { Box, Button, CircularProgress, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, FormGroup, InputAdornment, Radio, TextField, Typography } from "@mui/material";
import { useMemo, useRef, useState } from "react";
import useDebounce from "../../../lib/useDebounce";
import { useQuery } from "react-query";
import axiosClient from "../../../lib/axios";
import { IProcuradores, IDataPesquisarProcuradores } from "../../../types/api/geral/pesquisar-procuradores";
import BtnCarregarDados from "../../BtnCarregarDados";
import SearchIcon from "@mui/icons-material/Search";
import DisplayNotFoundInfo from "../../DisplayNotFindInfo";


export default function DialogContentSelectProcuradores({
    extraParams = {},
    onSelect = () => { },
    handleClose,
}: DialogContentSelectProcuradoresProps) {

    const inputRef = useRef<HTMLInputElement>(null);

    const [limit, setLimit] = useState(20);
    const [palavraChave, setPalavraChave] = useState("");
    const [selecionados, setSelecionados] = useState<IDataPesquisarProcuradores | null>(null);

    const debouncedKeyword = useDebounce(palavraChave);

    const { data, isFetching, isLoading } = useQuery({
        queryKey: [`select-procuradores`, debouncedKeyword, limit, extraParams],
        queryFn: () => axiosClient.get('/pesquisar-procuradores', {
            params: {
                limit,
                page: 1,
                pesquisar: debouncedKeyword,
                ...extraParams,
            },
        }),
        onSuccess: () => {
            inputRef.current?.focus();
        },
        cacheTime: 0,
    });

    const { reqProcuradores, dataProcuradores } = useMemo<IProcuradores>(
        () => ({
            reqProcuradores: data?.data,
            dataProcuradores: data?.data.data,
        }),
        [data]
    );

    return (
        <>
            <DialogTitle>Selecionar procurador</DialogTitle>
            <DialogContent sx={{ mt: { xs: 2, md: 0 } }}>
                <Box display="flex" alignItems="center" gap={2} py={2}>
                    <TextField
                        value={palavraChave}
                        fullWidth
                        inputRef={inputRef}
                        label="Pesquisar"
                        slotProps={{
                            htmlInput: { sx: { textTransform: "uppercase", tabIndex: 0 } },
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <SearchIcon />
                                    </InputAdornment>
                                )
                            }
                        }}
                        onChange={(e) => setPalavraChave(e.target.value)}
                    />
                </Box>

                <Divider sx={{ mb: 2 }} />

                {isLoading && (
                    <Box display="flex" justifyContent="center" my={2}>
                        <CircularProgress />
                    </Box>
                )}

                {!isLoading && (
                    <FormGroup>
                        <Box
                            display="flex"
                            overflow="auto"
                            height={{ xs: 550, md: 350 }}
                            flexDirection="column"
                        >
                            {dataProcuradores && dataProcuradores.length <= 0 && (
                                <DisplayNotFoundInfo label="Nenhum procurador encontrado" />
                            )}

                            {dataProcuradores &&
                                dataProcuradores.length > 0 &&
                                dataProcuradores.map((i: IDataPesquisarProcuradores, index: number) => {
                                    return (
                                        <>
                                            <FormControlLabel
                                                key={index}
                                                onChange={() => setSelecionados(i)}
                                                control={
                                                    <Radio checked={selecionados?.id === i.id} />
                                                }
                                                sx={{ mb: 0, pl: 1 }}
                                                label={
                                                    <Box>
                                                        <Typography>{`${i.nome}`}</Typography>
                                                    </Box>
                                                }
                                            />
                                        </>
                                    );
                                })}
                            <BtnCarregarDados
                                {...{ limit, isFetching }}
                                total={reqProcuradores.total}
                                action={() => setLimit((prevState) => prevState + 20)}
                            />
                        </Box>
                    </FormGroup>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    variant="outlined"
                    onClick={handleClose}
                >
                    Cancelar
                </Button>
                <Button
                    autoFocus
                    variant="contained"
                    onClick={() => {
                        if (selecionados) {
                            onSelect(selecionados);
                            handleClose();
                        }
                    }}
                    disabled={!selecionados}
                >
                    Selecionar
                </Button>
            </DialogActions>
        </>
    )
}

interface DialogContentSelectProcuradoresProps {
    extraParams?: { [key: string]: any };
    onSelect?: (item: IDataPesquisarProcuradores) => void;
    handleClose: () => void;
}
